/** The admin console (K4). Parts shared with workspace settings live in settingsCopy. */
export const adminCopy = {
  retry: 'Retry',
  loadFailed: {
    workspaces: 'Could not load workspaces.',
    users: 'Could not load users.',
    members: 'Could not load members.',
    topics: 'Could not load topics.',
    templates: 'Could not load templates.',
    providers: 'Could not load providers.',
    logs: 'Could not load logs.',
    status: 'Could not load system status.',
  },
  refreshFailed: {
    users: 'Could not refresh users.',
    workspaces: 'Could not refresh workspaces.',
    members: 'Could not refresh members.',
    topics: 'Could not refresh topics.',
    agents: 'Could not refresh agents.',
    templates: 'Could not refresh templates.',
    logs: 'Could not refresh logs.',
    status: 'Could not refresh status.',
  },
  expandWorkspace: (name: string) => `Expand ${name}`,
  page: {
    title: 'Admin',
    sections: 'Administration sections',
    tabs: {
      users: 'Users',
      workspaces: 'Workspaces',
      providers: 'Providers',
      agents: 'Agents',
      usage: 'Usage',
      runs: 'Runs',
      logs: 'Logs',
      system: 'System',
    },
    newProvider: 'New provider',
  },
  noWorkspaces: 'No workspaces found.',
  workspacePicker: { label: 'Workspace', empty: 'No workspaces' },
  providers: {
    description: 'System-wide model provider credentials',
    empty: 'No providers yet.',
  },
  users: {
    heading: 'Users',
    listLabel: 'Users',
    add: 'Add user',
    username: 'Username',
    password: 'Password',
    create: 'Create',
    cancel: 'Cancel',
    createFailed: 'Failed to create user — check the username and try again.',
    empty: 'No users yet.',
    adminChip: 'Admin',
    adminRole: 'admin',
    makeAdmin: 'Make admin',
    removeAdmin: 'Remove admin',
    delete: 'Delete',
    deleteDialog: {
      title: 'Delete user',
      description: (username: string) =>
        `"${username}" loses access and the account is deleted. This cannot be undone.`,
      confirm: 'Delete',
    },
    deleteFailed: 'Failed to delete user — try again.',
    adminFailed: 'Failed to update admin status — try again.',
    removeOwnAdminFailed:
      'Failed to remove admin — ask another admin to remove yours.',
    lastAdminFailed:
      'Failed to remove admin — make another user an admin first.',
  },
  workspaces: {
    heading: 'Workspaces',
    empty: 'No workspaces yet.',
    listLabel: 'Workspaces',
    delete: 'Delete',
    retryDelete: 'Retry deletion',
    members: 'Members',
    topics: 'Topics',
    topicsLabel: 'Topics',
    userToAdd: 'User to add',
    selectUser: 'Select user…',
    role: 'Role for new member',
    add: 'Add',
    cancelAdd: 'Cancel adding member',
    addMember: 'Add member',
    noRouter: 'no router',
    edit: 'Edit',
    routerFailed: 'Failed to update the router — try again.',
  },
  router: {
    enable: 'Enable LLM router',
    provider: 'Provider',
    select: 'Select…',
    model: 'Model',
    save: 'Save',
    cancel: 'Cancel',
  },
  agents: {
    views: 'Agent administration views',
    tabs: { agents: 'Agents', templates: 'Templates' },
  },
  templates: {
    heading: 'Templates',
    listLabel: 'Templates',
    new: 'New agent',
    builtIn: 'Built-in',
    delete: 'Delete',
    empty: 'No templates yet; open an agent and choose Save as template.',
    deleteDialog: {
      title: 'Delete template',
      description: (name: string) => `Permanently deletes "${name}".`,
      confirm: 'Delete',
    },
    deleteFailed: 'Failed to delete template — try again.',
  },
  runs: { heading: 'Runs' },
  logs: {
    heading: 'Logs',
    caption: 'logs',
    localBuffer: 'local',
    levelLabel: 'Level',
    allLevels: 'All levels',
    bufferLabel: 'Log buffer',
    coordinator: 'Coordinator logs',
    workerOption: (workspaceId: string) => `Worker: ${workspaceId}`,
    workerTitle: (workspaceId: string) => `worker: ${workspaceId}`,
    requestLabel: 'Request id',
    requestPlaceholder: 'Request id',
    searchLabel: 'Filter by message, target or fields',
    searchPlaceholder: 'Filter logs',
    count: (shown: string, buffered: string, capacity: string) =>
      `${shown} of ${buffered} shown · cap ${capacity} · refreshes every 3s`,
    refreshes: 'refreshes every 3s',
    countNoun: 'entries',
    clearFilters: 'Clear filters',
    /** Names every active filter: level, request id and text. */
    empty: (level: string, requestId: string, text: string) =>
      [
        level ? `No ${level} entries` : 'No log entries',
        requestId ? ` for request ${requestId}` : '',
        text ? ` match "${text}"` : '',
        '.',
      ].join(''),
  },
  system: {
    heading: 'System',
    refreshes: '· refreshes every 5s',
    stats: {
      mode: 'Mode',
      actors: 'Live actors',
      turns: 'Active turns',
      workers: 'Workers',
      subscribers: 'Subscribers',
      eventBuffer: 'Event buffer',
      dbPool: 'DB pool',
      topics: 'Sub. topics',
    },
    actorsHeading: 'Live actors',
    turnsHeading: 'In-flight turns',
    workersHeading: 'Workers',
    actors: 'Live agent actors',
    stale: 'stale',
    turns: 'In-flight turns',
    workers: 'Workers',
    noActors: 'No actors running.',
    columns: {
      agent: 'Agent',
      name: 'Name',
      workspace: 'Workspace',
      topic: 'Topic',
      thread: 'Thread',
      age: 'Age',
      address: 'Address',
      pid: 'PID',
      heartbeat: 'Heartbeat age',
    },
  },
} as const
